import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Pagination,
} from '@mui/material';
import { Add, Edit, Search as SearchIcon, Close as CloseIcon, FolderOff, Download, History, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const OrderManagement = ({ patients, onUpdate }) => {
  const navigate = useNavigate();
  const [patientOrders, setPatientOrders] = useState({});
  const [tests, setTests] = useState([]);
  const [clientSelectDialogOpen, setClientSelectDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    patientId: '',
    testId: '',
    orderDate: new Date(),
    notes: '',
  });
  const itemsPerPage = 10;

  useEffect(() => {
    fetchTests();
    if (patients.length > 0) {
      patients.forEach((patient) => {
        fetchPatientOrders(patient._id);
      });
    }
  }, [patients]);

  const fetchTests = async () => {
    try {
      const response = await api.get('/practitioner/tests');
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const fetchPatientOrders = async (patientId) => {
    try {
      const response = await api.get(`/practitioner/patient-tests/${patientId}`);
      setPatientOrders((prev) => ({ ...prev, [patientId]: response.data }));
    } catch (error) {
      console.error('Error fetching patient orders:', error);
    }
  };

  const handleCreateOrderClick = () => {
    setClientSelectDialogOpen(true);
    setClientSearchQuery('');
    setSelectedClientId('');
  };

  const handleClientSelect = (client) => {
    setClientSelectDialogOpen(false);
    // Navigate to checkout page with patient data in state
    navigate('/practitioner/checkout', { 
      state: { selectedPatient: client, fromStartOrder: true } 
    });
  };

  const handleClientSelectFromDropdown = () => {
    if (selectedClientId) {
      const client = patients.find(p => p._id === selectedClientId);
      if (client) {
        handleClientSelect(client);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPatient(null);
    setEditingOrderId(null);
    setFormData({
      patientId: '',
      testId: '',
      orderDate: new Date(),
      notes: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingOrderId) {
        // Update existing order
        const updateData = {
          testDate: formData.orderDate.toISOString(),
          notes: formData.notes,
        };

        await api.put(`/practitioner/patient-tests/${editingOrderId}`, updateData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Order updated successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        // Create new order
        await api.post('/practitioner/assign-test', {
          patientId: formData.patientId,
          testId: formData.testId,
          testDate: formData.orderDate.toISOString(),
          notes: formData.notes,
        });
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Order started successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      }
      handleCloseDialog();
      if (selectedPatient) {
        fetchPatientOrders(selectedPatient._id);
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || (editingOrderId ? 'Failed to update order' : 'Failed to create order'),
        confirmButtonColor: '#1976d2',
      });
    }
  };

  const filteredClients = patients.filter((client) => {
    if (!clientSearchQuery.trim()) return true;
    const query = clientSearchQuery.toLowerCase();
    return (
      client.firstName.toLowerCase().includes(query) ||
      client.lastName.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      (client.phone && client.phone.includes(query))
    );
  });

  // Clear selection if selected client is filtered out
  useEffect(() => {
    if (selectedClientId && !filteredClients.find(c => c._id === selectedClientId)) {
      setSelectedClientId('');
    }
  }, [clientSearchQuery, selectedClientId, filteredClients]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'results-in':
        return 'success';
      case 'in-progress':
        return 'warning';
      case 'pending':
      case 'assigned':
        return 'info';
      case 'reviewed':
        return 'info';
      case 'demo-order':
        return 'primary';
      case 'pending-payment':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusChipSx = (status) => {
    const normalized = (status || '').toLowerCase();
    const styles = {
      pending: { backgroundColor: '#f5f5f5', color: '#424242' },
      assigned: { backgroundColor: '#e3f2fd', color: '#0d47a1' },
      'in-progress': { backgroundColor: '#fff8e1', color: '#f57f17' },
      completed: { backgroundColor: '#e8f5e9', color: '#1b5e20' },
      'results-in': { backgroundColor: '#e0f2f1', color: '#004d40' },
      reviewed: { backgroundColor: '#e3f2fd', color: '#0b5394' },
      'demo-order': { backgroundColor: '#e1f5fe', color: '#01579b' },
      'pending-payment': { backgroundColor: '#fff8e1', color: '#ff6f00' },
      cancelled: { backgroundColor: '#ffebee', color: '#b71c1c' },
    };
    return styles[normalized] || { backgroundColor: '#f5f5f5', color: '#616161' };
  };

  const isReportUploaded = (order) => Boolean(order?.fileUrl);

  // "New" for practitioner: order started but no report uploaded yet.
  // Prefer `createdAt` recency when available, but still highlight even if missing.
  const isNewOrder = (order) => {
    if (!order) return false;
    if (isReportUploaded(order)) return false;
    const normalizedStatus = String(order.status || '').toLowerCase();
    const isOpenStatus = normalizedStatus === 'pending' || normalizedStatus === 'assigned' || normalizedStatus === 'in-progress';
    if (!isOpenStatus) return false;

    const createdAt = order.createdAt ? new Date(order.createdAt) : null;
    if (!createdAt || Number.isNaN(createdAt.getTime())) return true;

    const hours = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
    return hours <= 72;
  };

  const getRowSx = (order) => {
    const normalizedStatus = String(order?.status || '').toLowerCase();
    if (normalizedStatus === 'cancelled') {
      return {
        backgroundColor: 'rgba(198, 40, 40, 0.10)',
        '&:hover': { backgroundColor: 'rgba(198, 40, 40, 0.16)' },
      };
    }
    if (normalizedStatus === 'completed') {
      return {
        backgroundColor: 'rgba(46, 125, 50, 0.10)',
        '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
      };
    }
    if (isNewOrder(order)) {
      return {
        backgroundColor: 'rgba(25, 118, 210, 0.10)',
        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.16)' },
      };
    }
    return {
      backgroundColor: '#fff',
      '&:hover': { backgroundColor: '#f9f9f9' },
    };
  };

  const getFilteredOrders = () => {
    let allOrders = [];
    Object.keys(patientOrders).forEach((patientId) => {
      if (patientOrders[patientId]) {
        allOrders = allOrders.concat(
          patientOrders[patientId].map((order) => ({
            ...order,
            patientId,
          }))
        );
      }
    });

    // Filter by tab
    let filtered = allOrders;
    switch (activeTab) {
      case 'drafts':
        filtered = allOrders.filter((order) => order.status === 'pending');
        break;
      case 'in-progress':
        filtered = allOrders.filter(
          (order) => order.status === 'assigned' || order.status === 'in-progress'
        );
        break;
      case 'results-in':
        filtered = allOrders.filter((order) => order.status === 'completed');
        break;
      case 'reviewed':
        filtered = allOrders.filter((order) => order.status === 'reviewed');
        break;
      case 'all':
      default:
        filtered = allOrders;
    }

    // Filter by patient search
    if (orderSearchQuery.trim()) {
      const query = orderSearchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        const patientName = `${order.patient?.firstName} ${order.patient?.lastName}`.toLowerCase();
        return patientName.includes(query);
      });
    }

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  const refreshOrderData = async (patientId) => {
    if (patientId) {
      await fetchPatientOrders(patientId);
    }
    if (onUpdate) onUpdate();
  };

  const getReportAbsoluteUrl = (fileUrl) => {
    if (!fileUrl) return null;
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;

    const baseURL = api?.defaults?.baseURL || '';
    const origin = /^https?:\/\//i.test(baseURL) ? new URL(baseURL).origin : window.location.origin;
    return `${origin}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
  };

  const handleDownloadResult = async (orderId) => {
    let response;
    let didTriggerDownload = false;
    try {
      response = await api.get(`/practitioner/orders/${orderId}/download-result`, { responseType: 'blob' });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const fileNameMatch =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        contentDisposition.match(/filename="?([^"]+)"?/i);

      let fileName = `lab-result-${orderId}`;
      if (fileNameMatch?.[1]) {
        try {
          fileName = decodeURIComponent(fileNameMatch[1]);
        } catch {
          // If header isn't valid URI-encoded, just use it as-is.
          fileName = fileNameMatch[1];
        }
      }

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      didTriggerDownload = true;
      try {
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch {
        // If cleanup fails, don't show an error after a successful download.
      }
    } catch (error) {
      // Axios can throw for non-2xx even when it returned a Blob (e.g. PDF) in error.response.data.
      const blobFromError = error?.response?.data;
      const isBlob =
        typeof Blob !== 'undefined' &&
        blobFromError instanceof Blob &&
        typeof blobFromError.size === 'number' &&
        blobFromError.size > 0;

      if (isBlob) {
        const contentDisposition = error.response?.headers?.['content-disposition'] || '';
        const fileNameMatch =
          contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
          contentDisposition.match(/filename="?([^"]+)"?/i);

        let fileName = `lab-result-${orderId}`;
        if (fileNameMatch?.[1]) {
          try {
            fileName = decodeURIComponent(fileNameMatch[1]);
          } catch {
            fileName = fileNameMatch[1];
          }
        }

        const blobUrl = window.URL.createObjectURL(blobFromError);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        didTriggerDownload = true;
        try {
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        } catch {
          // ignore cleanup errors
        }
        return;
      }

      // If the browser download was already triggered, don't show a failure toast.
      if (didTriggerDownload) return;
      // Intentionally silent: avoid showing "Download failed" popup.
      console.error('Download result failed:', error);
    }
  };

  const handleReviewAndShare = async (order) => {
    const isResend = Boolean(order.patientSharedAt);
    const result = await SwalWithA11yFix({
      title: isResend ? 'Resend Report' : 'Send Report',
      text: 'Add remarks (optional) and send this report to the patient dashboard.',
      input: 'textarea',
      inputValue: order.practitionerRemarks || '',
      inputPlaceholder: 'Enter remarks for patient...',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: isResend ? 'Resend to Patient' : 'Send to Patient',
    });
    if (!result.isConfirmed) return;
    try {
      await api.post(`/practitioner/orders/${order._id}/review-share`, {
        remarks: result.value || '',
      });
      SwalWithA11yFix({
        icon: 'success',
        title: isResend ? 'Resent!' : 'Sent!',
        text: isResend
          ? 'Report has been resent to the patient dashboard.'
          : 'Report has been sent to the patient dashboard.',
        timer: 2000,
        showConfirmButton: false,
      });
      await refreshOrderData(order.patientId);
    } catch (error) {
      SwalWithA11yFix({ icon: 'error', title: 'Share failed', text: error.response?.data?.message || 'Failed to share result.' });
    }
  };

  const handleViewHistory = async (order) => {
    try {
      const response = await api.get(`/practitioner/orders/${order._id}/history`);
      const historyRows = (response.data || []).map((entry) => {
        const actor = entry.actor ? `${entry.actor.firstName || ''} ${entry.actor.lastName || ''}`.trim() : entry.actorRole;
        return `<div style="text-align:left;margin-bottom:8px;"><strong>${new Date(entry.timestamp).toLocaleString()}</strong><br/>${entry.action} by ${actor}${entry.note ? ` - ${entry.note}` : ''}</div>`;
      }).join('');
      SwalWithA11yFix({
        title: 'Order History',
        html: historyRows || 'No history yet.',
        width: 700,
      });
    } catch (error) {
      SwalWithA11yFix({ icon: 'error', title: 'History failed', text: error.response?.data?.message || 'Failed to load history.' });
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, orderSearchQuery, statusFilter]);

  const getEmptyMessage = () => {
    const tabLabels = {
      drafts: 'Drafts',
      'in-progress': 'In Progress',
      'results-in': 'Results In',
      reviewed: 'Reviewed',
      all: 'All',
    };
    const tabName = tabLabels[activeTab] || 'All';
    return `We were unable to find any ${tabName} orders for you.`;
  };

  return (
    <Box>
      {/* Start Order Button */}
      {patients.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              placeholder="Search orders..."
              variant="outlined"
              size="small"
              value={orderSearchQuery}
              onChange={(e) => setOrderSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
              }}
              sx={{
                width: { xs: '100%', sm: 250 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateOrderClick}
              sx={{
                backgroundColor: '#1976d2',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                },
              }}
            >
              Start an Order
            </Button>
          </Box>
        </Box>
      )}

      {patients.length === 0 ? (
        <Paper 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No clients assigned. Assign clients first to create orders.
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {/* Tabs Section */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  minWidth: 'auto',
                  px: 2,
                  color: '#666',
                  '&.Mui-selected': {
                    color: '#1976d2',
                  },
                },
              }}
            >
              <Tab label="All" value="all" />
              <Tab label="Reviewed" value="reviewed" />
              <Tab label="Results In" value="results-in" />
              <Tab label="In Progress" value="in-progress" />
              <Tab label="Drafts" value="drafts" />
            </Tabs>
          </Box>

          {/* Status Filter */}
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
            <FormControl variant="outlined" size="small" sx={{ width: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select 
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label="New order"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.10)',
                  border: '1px solid rgba(25, 118, 210, 0.35)',
                }}
              />
              <Chip
                size="small"
                label="Report uploaded"
                sx={{
                  backgroundColor: 'rgba(46, 125, 50, 0.10)',
                  border: '1px solid rgba(46, 125, 50, 0.35)',
                }}
              />
            </Box>
          </Box>

          {/* Orders Content */}
          <Box sx={{ p: 2 }}>
            {filteredOrders.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <FolderOff sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  We were unable to find any {getEmptyMessage().split(' ').slice(5, -14).join(' ')} orders for you.{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: '#1976d2',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={() => setActiveTab('all')}
                  >
                    Search all orders.
                  </Typography>
                </Typography>
              </Box>
            ) : (
              <TableContainer
                sx={{
                  maxWidth: '100%',
                  overflowX: 'auto',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Patient</TableCell>
                      <TableCell>Test Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Order Date</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((order) => (
                      <TableRow 
                        key={order._id}
                        sx={getRowSx(order)}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {order.patient?.firstName} {order.patient?.lastName}
                        </TableCell>
                        <TableCell>{order.test?.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status.replace('-', ' ')}
                            color={getStatusColor(order.status)}
                            size="small"
                            variant="filled"
                            sx={getStatusChipSx(order.status)}
                          />
                        </TableCell>
                        <TableCell>
                          {order.testDate
                            ? new Date(order.testDate).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>{order.notes || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleViewHistory(order)} title="View history">
                            <History />
                          </IconButton>
                          {order.fileUrl && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const url = getReportAbsoluteUrl(order.fileUrl);
                                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                                title="View report"
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDownloadResult(order._id)} title="Download result">
                                <Download />
                              </IconButton>
                              <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={() => handleReviewAndShare(order)}>
                                {order.patientSharedAt ? 'Resend' : 'Send'}
                              </Button>
                            </>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => {
                              const patient = patients.find((p) => p._id === order.patientId);
                              setSelectedPatient(patient);
                              setEditingOrderId(order._id);
                              setFormData({
                                patientId: order.patientId,
                                testId: order.test?._id,
                                orderDate: order.testDate
                                  ? new Date(order.testDate)
                                  : new Date(),
                                notes: order.notes || '',
                              });
                              setDialogOpen(true);
                            }}
                            title="Edit Order"
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {filteredOrders.length > 0 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {((page - 1) * itemsPerPage) + 1} - {Math.min(page * itemsPerPage, filteredOrders.length)} of {filteredOrders.length}
                </Typography>
                <Pagination
                  count={Math.ceil(filteredOrders.length / itemsPerPage)}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Choose or Create Client Dialog */}
      <Dialog 
        open={clientSelectDialogOpen} 
        onClose={() => setClientSelectDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Choose Client for Order
        </DialogTitle>
        <DialogContent>
          {/* Search Field */}
          {patients.length > 0 && (
            <Box sx={{ mb: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <SearchIcon 
                  sx={{ 
                    position: 'absolute', 
                    left: 12, 
                    color: '#666',
                    fontSize: 20 
                  }} 
                />
                <TextField
                  fullWidth
                  placeholder="Search clients by name, email, or phone..."
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      paddingLeft: '0px',
                      '& input': {
                        paddingLeft: 4
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: clientSearchQuery && (
                      <CloseIcon
                        onClick={() => setClientSearchQuery('')}
                        sx={{
                          cursor: 'pointer',
                          color: '#999',
                          fontSize: 18,
                          mr: 1,
                          '&:hover': {
                            color: '#333',
                          }
                        }}
                      />
                    ),
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Existing Clients Dropdown */}
          {patients.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
                Select Existing Client
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Choose a client</InputLabel>
                <Select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  label="Choose a client"
                >
                  {filteredClients.length === 0 ? (
                    <MenuItem disabled>
                      {clientSearchQuery ? 'No clients match your search' : 'No clients available'}
                    </MenuItem>
                  ) : (
                    filteredClients.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {client.firstName} {client.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.email} • {client.phone}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {filteredClients.length === 0 && clientSearchQuery && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  No clients found matching "{clientSearchQuery}"
                </Typography>
              )}
            </Box>
          )}

          {/* Create New Client Option */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
              Don't see your client?
            </Typography>
            <Button 
              fullWidth
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                SwalWithA11yFix({
                  icon: 'info',
                  title: 'Create New Client',
                  text: 'New clients can be created from the Clients section',
                  confirmButtonColor: '#1976d2',
                });
              }}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#1565c0',
                }
              }}
            >
              Add New Client
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setClientSelectDialogOpen(false);
              setSelectedClientId('');
              setClientSearchQuery('');
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          {selectedClientId && (
            <Button 
              onClick={handleClientSelectFromDropdown}
              variant="contained"
              sx={{
                backgroundColor: '#1976d2',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#1565c0',
                }
              }}
            >
              Continue
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          {editingOrderId ? 'Edit Order' : 'Start an Order'} for {selectedPatient?.firstName} {selectedPatient?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!editingOrderId && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Test</InputLabel>
                <Select
                  value={formData.testId}
                  onChange={(e) => setFormData({ ...formData, testId: e.target.value })}
                  label="Select Test"
                  required
                >
                  {tests.map((test) => (
                    <MenuItem key={test._id} value={test._id}>
                      {test.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Order Date"
                value={formData.orderDate}
                onChange={(date) => setFormData({ ...formData, orderDate: date })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal'
                  }
                }}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={!editingOrderId && !formData.testId}
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: '#bdbdbd',
              },
            }}
          >
            {editingOrderId ? 'Update' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderManagement;

